"use client"
import { classNames, useIsGreaterThanOrEqualToLg } from "@opensea/ui-kit"
import {
  useDebounceCallback,
  useEventListener,
  useMountEffect,
} from "@opensea/ui-kit/hooks"
import { useEffect, useState } from "react"
import { useShallow } from "zustand/react/shallow"
import { useTopNavbarStore } from "@/stores/use-top-navbar-store"
import { useTopNavbarVariants } from "./utils"

const SCROLL_DEBOUNCE = 50

export function useTopNavbarState({ isMobile }: { isMobile: boolean }) {
  const variants = useTopNavbarVariants({ isMobile })
  const { height } = useTopNavbarStore(
    useShallow(state => ({ height: state.height })),
  )

  // TODO: can we rework this to use useIsSticky?
  const [scrollPercentage, setScrollPercentage] = useState(0)
  const scrollHandler = useDebounceCallback(
    () => {
      let rafId: number | null = null

      rafId = window.requestAnimationFrame(() => {
        setScrollPercentage(Math.min(window.scrollY / 50, 1))
        rafId = null
      })

      return () => {
        if (rafId !== null) {
          window.cancelAnimationFrame(rafId)
        }
      }
    },
    SCROLL_DEBOUNCE,
    { leading: true, maxWait: SCROLL_DEBOUNCE },
  )

  useMountEffect(() => {
    window.addEventListener("scroll", scrollHandler)
    scrollHandler()

    return () => {
      window.removeEventListener("scroll", scrollHandler)
    }
  })

  return {
    height,
    variants,
    scrollPercentage,
  }
}

export function useTopNavbarSkeletonClassNames() {
  const { variants, scrollPercentage } = useTopNavbarState({ isMobile: false })

  if (variants.transparent !== "always") {
    return ""
  }

  return classNames(
    variants.dark && variants.gradient && scrollPercentage === 0
      ? "bg-bg-contrast-1"
      : "bg-bg-additional-3",
  )
}

function useTopNavbarHeight() {
  const isLg = useIsGreaterThanOrEqualToLg()

  const height = isLg ? 64 : 68

  return {
    height,
    base: height,
    offset: 0,
  }
}

export function useUpdateTopNavHeight(
  callback: (height: number, offset: number) => number | undefined,
  dependencies: React.DependencyList = [],
) {
  const { setHeight: setTopNavbarHeight } = useTopNavbarStore(
    useShallow(state => ({ setHeight: state.setHeight })),
  )
  const { base: topNavHeight, offset: topNavOffset } = useTopNavbarHeight()

  function evaluateHeight() {
    const height = callback(topNavHeight, topNavOffset)

    setTopNavbarHeight(height)
  }

  useEffect(evaluateHeight, dependencies)

  useEventListener("scroll", evaluateHeight)
  useMountEffect(() => {
    evaluateHeight()

    return () => {
      setTopNavbarHeight(undefined)
    }
  })
}
